/* Clovia core: small custom elements, no framework, no dependencies.
   Security: store strings are never injected with innerHTML. Server-rendered
   section HTML (already escaped by Liquid) is parsed with DOMParser. */

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const strings = () => (window.Clovia && window.Clovia.strings) || {};
const routes = () => (window.Clovia && window.Clovia.routes) || { root: '/', cart: '/cart', cartAdd: '/cart/add' };

export async function fetchSection(url, sectionId, signal) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u, { signal, headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error(`Section ${sectionId} failed: ${res.status}`);
  return new DOMParser().parseFromString(await res.text(), 'text/html');
}

/* ---------- Toast ---------- */
let toastTimer;
export function toast(message, link) {
  const el = $('[data-toast]');
  if (!el) return;
  const text = document.createElement('span');
  text.textContent = message;
  const nodes = [text];
  if (link) {
    const a = document.createElement('a');
    a.href = link.href;
    a.className = 'link';
    a.textContent = link.label;
    nodes.push(a);
  }
  el.replaceChildren(...nodes);
  el.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('is-visible'), 4200);
}

/* ---------- Cart count ---------- */
export async function refreshCartCount(count) {
  if (typeof count !== 'number') {
    try {
      const res = await fetch(`${routes().root.replace(/\/$/, '')}/cart.js`, { headers: { Accept: 'application/json' } });
      count = (await res.json()).item_count;
    } catch {
      return;
    }
  }
  $$('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0;
  });
}

/* ---------- Cart: one small API shared by product forms, Shop the Look and the drawer ---------- */
const CART_SECTION = 'cart-drawer';
const cartDrawer = () => $('cart-drawer');

async function cartPost(url, body) {
  const isForm = body instanceof FormData;
  if (cartDrawer()) {
    if (isForm) body.append('sections', CART_SECTION);
    else body.sections = CART_SECTION;
  }
  const res = await fetch(`${url}.js`, {
    method: 'POST',
    headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: isForm ? body : JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.description || json.message || strings().addError);
  return json;
}

const cart = {
  /** Add a FormData product form, or an array of { id, quantity }. */
  async add(payload, { open = true } = {}) {
    const body = await cartPost(routes().cartAdd, payload instanceof FormData ? payload : { items: payload });
    document.dispatchEvent(new CustomEvent('clovia:cart-added', { detail: body }));
    if (open) cart.show(body);
    return body;
  },
  async change(key, quantity) {
    const body = await cartPost(routes().cartChange, { id: key, quantity });
    cart.render(body.sections);
    if (typeof body.item_count === 'number') refreshCartCount(body.item_count);
    return body;
  },
  /** Re-render the drawer from a response and open it; toast when the drawer is off. */
  show(body) {
    const drawer = cartDrawer();
    if (drawer && body?.sections?.[CART_SECTION]) {
      cart.render(body.sections);
      drawer.open();
    } else {
      refreshCartCount();
      toast(strings().added, { href: routes().cart, label: strings().viewBag });
    }
  },
  render(sections) {
    const html = sections?.[CART_SECTION];
    if (html) cartDrawer()?.render(html);
  },
};
window.Clovia = Object.assign(window.Clovia || {}, { cart });

/* Product forms post normally without JS; with JS they add in place. */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-product-form]');
  if (!form) return;
  event.preventDefault();
  // Buy now: a cart permalink goes straight to checkout with only this piece.
  if (event.submitter?.hasAttribute('data-buy-now')) {
    const data = new FormData(form);
    const qty = Math.max(1, Number(data.get('quantity')) || 1);
    event.submitter.classList.add('is-loading');
    window.location.assign(`${routes().root.replace(/\/$/, '')}/cart/${data.get('id')}:${qty}`);
    return;
  }
  const button = form.querySelector('[data-add-button]') || form.querySelector('[type="submit"]');
  const error = form.querySelector('[data-form-error]');
  button?.classList.add('is-loading');
  button?.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;
  try {
    await cart.add(new FormData(form));
  } catch (err) {
    if (error) {
      error.textContent = err.message || strings().addError;
      error.hidden = false;
    } else {
      toast(err.message || strings().addError);
    }
  } finally {
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
  }
});

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = $('dialog', this);
    this.timers = new Map();
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
  }

  open() {
    openDrawer(this.dialog, document.activeElement);
  }

  render(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-contents]');
    const current = $('[data-cart-contents]', this);
    if (!fresh || !current) return;
    const hadFocus = current.contains(document.activeElement) ? document.activeElement.id : null;
    current.replaceWith(fresh);
    refreshCartCount(Number(fresh.dataset.count));
    if (hadFocus) document.getElementById(hadFocus)?.focus();
    else if (this.dialog.open && !this.dialog.contains(document.activeElement)) $('[data-close-drawer]', this)?.focus();
  }

  onChange = (event) => {
    const input = event.target.closest('[data-line-qty]');
    if (!input) return;
    const key = input.dataset.lineQty;
    clearTimeout(this.timers.get(key));
    this.timers.set(key, setTimeout(() => this.update(key, Math.max(0, Number(input.value) || 0)), 400));
  };

  onClick = (event) => {
    const remove = event.target.closest('[data-line-remove]');
    if (remove) this.update(remove.dataset.lineRemove, 0);
  };

  async update(key, quantity) {
    const contents = $('[data-cart-contents]', this);
    contents?.setAttribute('aria-busy', 'true');
    try {
      await cart.change(key, quantity);
      const status = $('[data-cart-status]', this);
      if (status) status.textContent = strings().bagUpdated || '';
    } catch (err) {
      toast(err.message || strings().addError);
      contents?.removeAttribute('aria-busy');
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/* ---------- Product card: dynamic colour swatches ---------- */
class ProductCard extends HTMLElement {
  connectedCallback() {
    this.image = $('[data-card-image]', this);
    this.media = $('.card__media', this);
    this.link = $('[data-card-link]', this);
    this.name = $('[data-swatch-name]', this);
    this.hover = $('[data-card-hover]', this);
    if (!this.image && !$('[data-swatch]', this)) return;
    this.original = this.snapshot();
    this.addEventListener('click', this.onClick);
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointerover', this.onPreview);
      this.addEventListener('pointerleave', this.restore);
    }
  }

  snapshot() {
    return {
      src: this.image?.src,
      srcset: this.image?.srcset,
      href: this.link?.href,
      name: this.name?.textContent,
      tint: this.media?.style.getPropertyValue('--card-tint'),
      hoverSrcset: this.hover?.hidden ? '' : this.hover?.getAttribute('srcset') || '',
    };
  }

  onClick = (event) => {
    const swatch = event.target.closest('[data-swatch]');
    if (!swatch) return;
    event.preventDefault();
    $$('[data-swatch]', this).forEach((s) => s.setAttribute('aria-pressed', String(s === swatch)));
    this.show(swatch);
    this.original = { ...this.snapshot(), src: swatch.dataset.src || this.original.src, srcset: swatch.dataset.srcset || this.original.srcset, hoverSrcset: swatch.dataset.hoverSrcset };
  };

  onPreview = (event) => {
    const swatch = event.target.closest('[data-swatch]');
    if (swatch) this.show(swatch);
  };

  restore = () => this.apply(this.original);

  show(swatch) {
    const { src, srcset, url, name, tint, hoverSrcset } = swatch.dataset;
    this.apply({ src, srcset, href: url, name, tint, hoverSrcset });
  }

  apply({ src, srcset, href, name, tint, hoverSrcset }) {
    if (href && this.link) this.link.href = href;
    if (hoverSrcset !== undefined) this.setHover(hoverSrcset);
    if (name && this.name) this.name.textContent = name;
    if (tint !== undefined && this.media) this.media.style.setProperty('--card-tint', tint);
    if (!src || !this.image || this.image.src === src) return;
    // Preload, then crossfade, so the card never flashes an empty frame.
    const next = new Image();
    next.sizes = this.image.sizes;
    if (srcset) next.srcset = srcset;
    next.src = src;
    const swap = () => {
      this.image.classList.add('is-swapping');
      requestAnimationFrame(() => {
        if (srcset) this.image.srcset = srcset;
        this.image.src = src;
        requestAnimationFrame(() => this.image.classList.remove('is-swapping'));
      });
    };
    next.decode ? next.decode().then(swap, swap) : swap();
  }

  // The hover close-up always follows the chosen colour. A colour without one shows no hover
  // image at all, so the card never fades to a different colour.
  setHover(srcset) {
    if (!this.hover) return;
    if (!srcset) {
      this.hover.hidden = true;
      return;
    }
    if (this.hover.getAttribute('srcset') !== srcset) {
      this.hover.srcset = srcset;
      this.hover.src = srcset.split(',').pop().trim().split(' ')[0];
    }
    this.hover.hidden = false;
  }
}
customElements.define('product-card', ProductCard);

/* ---------- Drawers (menu, search) built on <dialog> ---------- */
function openDrawer(dialog, trigger) {
  if (!dialog || dialog.open) return;
  dialog._trigger = trigger;
  dialog.showModal();
  requestAnimationFrame(() => dialog.classList.add('is-open'));
  document.documentElement.style.overflow = 'hidden';
  (dialog.querySelector('[autofocus], input[type="search"]') || dialog.querySelector('[data-close-drawer]'))?.focus();
}
function closeDrawer(dialog) {
  if (!dialog?.open) return;
  dialog.classList.remove('is-open');
  const done = () => {
    dialog.close();
    document.documentElement.style.overflow = '';
    dialog._trigger?.focus();
  };
  reducedMotion() ? done() : setTimeout(done, 450);
}
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-open-drawer], [data-open-search]');
  if (opener) {
    const id = opener.hasAttribute('data-open-search') ? 'SearchDrawer' : opener.dataset.openDrawer;
    const dialog = document.getElementById(id);
    if (dialog) {
      event.preventDefault();
      openDrawer(dialog, opener);
    }
    return;
  }
  const closer = event.target.closest('[data-close-drawer]');
  if (closer) return closeDrawer(closer.closest('dialog'));
  if (event.target instanceof HTMLDialogElement && event.target.classList.contains('drawer')) closeDrawer(event.target);
});
document.addEventListener('cancel', (event) => {
  if (event.target.classList?.contains('drawer')) {
    event.preventDefault();
    closeDrawer(event.target);
  }
}, true);

/* ---------- Sticky header ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.firstElementChild;
    this.last = window.scrollY;
    this.threshold = this.header.classList.contains('site-header--overlay') ? 40 : 1;
    this.hideOnScroll = this.hasAttribute('data-hide-on-scroll');
    this.ticking = false;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
  onScroll = () => {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  };
  update() {
    const y = window.scrollY;
    this.header.classList.toggle('is-solid', y > this.threshold || this.contains(document.activeElement));
    if (this.hideOnScroll) this.header.classList.toggle('is-hidden', y > this.last && y > 480);
    this.last = y;
  }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- Predictive search ---------- */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = $('input[type="search"]', this);
    this.results = $('[data-predictive-results]', this);
    this.input?.addEventListener('input', this.onInput);
  }
  onInput = () => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.search(this.input.value.trim()), 220);
  };
  async search(q) {
    this.controller?.abort();
    if (q.length < 2) return this.results.replaceChildren();
    this.controller = new AbortController();
    try {
      const url = `${routes().predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
      const doc = await fetchSection(url, 'predictive-search', this.controller.signal);
      const fresh = doc.querySelector('[data-predictive-results]');
      if (fresh) this.results.replaceChildren(...fresh.childNodes);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn(err);
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* ---------- Quantity stepper ---------- */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-qty]');
  if (!btn) return;
  const input = btn.parentElement.querySelector('input[type="number"]');
  if (!input) return;
  const step = Number(btn.dataset.qty);
  const min = Number(input.min || 0);
  input.value = Math.max(min, Number(input.value || 0) + step);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------- Splash cleanup ---------- */
const splash = $('[data-splash]');
if (splash) {
  const root = document.documentElement;
  if (!root.classList.contains('splash-on')) splash.remove();
  else splash.addEventListener('animationend', (e) => {
    if (e.animationName !== 'splash-lift') return;
    root.classList.remove('splash-on');
    splash.remove();
  });
}

/* ---------- Brand cursor (desktop, precise pointer only) ---------- */
const cursorEl = $('[data-brand-cursor]');
if (cursorEl && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  import(cursorEl.dataset.src)
    .then((m) => m.initCursor(cursorEl))
    .catch(() => {});
}

/* ---------- Product recommendations (lazy, via Section Rendering) ---------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.children.length || !this.dataset.url) return;
    const load = async (url) => {
      try {
        const res = await fetch(url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.children.length) this.replaceChildren(...fresh.childNodes);
        else if (this.dataset.fallbackUrl && url !== this.dataset.fallbackUrl) load(this.dataset.fallbackUrl);
      } catch (err) {
        console.warn(err);
      }
    };
    new IntersectionObserver((entries, obs) => {
      if (!entries[0].isIntersecting) return;
      obs.disconnect();
      load(this.dataset.url);
    }, { rootMargin: '0px 0px 600px 0px' }).observe(this);
  }
}
customElements.define('product-recommendations', ProductRecommendations);
